import { useTranslation } from 'react-i18next'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { EarnMyAsset } from '../components/EarnMyAssets'
import { EARN_DASHBOARD_STATE, EarnAsset, EarnDashboardNoWallets } from '../types'
import { formatUsd, formatWholePercentage, toMyEarnAssets } from '../utils/utils'
import { useEarnDashboardQuery } from './api/useEarnDashboardQuery'
import { useEarnStatisticsQuery } from './api/useEarnStatisticsQuery'
import { useIsDirectEarning } from './useIsDirectEarning'

export interface UseEarnPageDataHandlers {
  onDeposit: (asset: EarnAsset) => void
  onWithdraw: (asset: EarnAsset) => void
}

interface EarnPageDataCommon {
  isStatisticsLoading: boolean
  isDirectEarning: boolean
  canManageDistribution: boolean
}

export type EarnPageData = EarnPageDataCommon &
  (
    | { dashboardState: typeof EARN_DASHBOARD_STATE.LOADING }
    | { dashboardState: typeof EARN_DASHBOARD_STATE.NO_PRODUCTS }
    | {
        dashboardState: typeof EARN_DASHBOARD_STATE.NO_WALLETS
        featuredProduct: EarnDashboardNoWallets['featuredProduct']
      }
    | {
        dashboardState: typeof EARN_DASHBOARD_STATE.NO_PORTFOLIOS
        assets: EarnMyAsset[]
      }
    | {
        dashboardState: typeof EARN_DASHBOARD_STATE.READY
        assets: EarnMyAsset[]
        epcTakesEarn: boolean
        distributionProductId: string | undefined
        summary: {
          allocatedFunds: string
          availableFunds: string
          totalAllTimeEarnings: string
        }
        totalEarnings: {
          allocationEarnings: string
          customerEarnings: string
          totalEarnings: string
        }
        customerRewardsProgram: {
          totalBalance: string
          optInRateLabel: string
          distributedEarnings: string
          remainingTotal: string
        }
      }
  )

export const useEarnPageData = ({
  onDeposit,
  onWithdraw,
}: UseEarnPageDataHandlers): EarnPageData => {
  const { t } = useTranslation()
  const { data, isLoading } = useEarnDashboardQuery()
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()
  const isDirectEarning = useIsDirectEarning()
  const canManageDistribution = !!useMappedRoles()?.earn?.canDistribute

  const buildAssets = (earnAssets: EarnAsset[], comingSoonAssets: EarnAsset[]) =>
    [...earnAssets, ...comingSoonAssets].map(
      toMyEarnAssets(t, onDeposit, onWithdraw, statistics?.currencies ?? {})
    )

  const common = { isStatisticsLoading, isDirectEarning, canManageDistribution }

  if (isLoading || !data) {
    return { ...common, dashboardState: EARN_DASHBOARD_STATE.LOADING }
  }

  if (data.state === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return { ...common, dashboardState: data.state }
  }

  if (data.state === EARN_DASHBOARD_STATE.NO_WALLETS) {
    return { ...common, dashboardState: data.state, featuredProduct: data.featuredProduct }
  }

  if (data.state === EARN_DASHBOARD_STATE.NO_PORTFOLIOS) {
    return {
      ...common,
      dashboardState: data.state,
      assets: buildAssets(data.earnAssets, data.comingSoonAssets),
    }
  }

  // Temporary: assumes a single earn asset. Once merchants can hold multiple,
  // this should become a user-selected asset (e.g. via a dropdown) for distribution.
  const activeEarnAsset = data.earnAssets[0]
  const merchantStatistics = activeEarnAsset
    ? statistics?.currencies[activeEarnAsset.asset]
    : undefined

  return {
    ...common,
    dashboardState: data.state,
    assets: buildAssets(data.earnAssets, data.comingSoonAssets),
    epcTakesEarn: activeEarnAsset?.epcTakesEarn ?? false,
    distributionProductId: activeEarnAsset?.productId ?? undefined,
    summary: {
      allocatedFunds: formatUsd(data.fundsOverview.allocatedFunds),
      availableFunds: formatUsd(statistics?.totals.merchantMovableBalance),
      totalAllTimeEarnings: formatUsd(data.totalEarnings.fromMyAllocation),
    },
    totalEarnings: {
      allocationEarnings: formatUsd(data.totalEarnings.fromMyAllocation),
      customerEarnings: formatUsd(data.totalEarnings.fromCustomers),
      totalEarnings: formatUsd(data.totalEarnings.total),
    },
    customerRewardsProgram: {
      totalBalance: formatUsd(statistics?.totals.customerEarningBalance),
      optInRateLabel: t('earn.optInRateLabel', {
        rate: formatWholePercentage(merchantStatistics?.optIn.rate ?? 0),
        count: merchantStatistics?.optIn.optedInCustomers ?? 0,
      }),
      distributedEarnings: formatUsd(data.customerRewardsProgram.distributedEarnings),
      remainingTotal: formatUsd(data.customerRewardsProgram.remainingTotal),
    },
  }
}
